import { useEffect, useState, useCallback } from 'react';
import { H4Bold, ButtonIconBg } from '@comeon/ui';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { authenticationSelector } from '~/components/authentication/ducks/selectors.js';
import Icon, { ICON_OPTIONS_HORIZONTAL } from '~/components/icon/index.js';
import useContextualPathTransformer from '../../../../hooks/use-contextual-path-transformer.js';
import { LeftSideIconType } from '../../../sidebar/sidebar-header/sidebar-header.js';
import { Sidebar, SidebarHeader, SidebarContent } from '../../../sidebar/index.js';
import useIntervalAction from '../../../../hooks/use-interval-action.js';
import SidebarScrollAware from '~/utils/sidebar-scroll-aware.js';
import { isBiggerSelector } from '../../../layout/mediaquery.js';
import useTranslations from '../../../translation/use-translations';
import { ROUTE_ACCOUNT, ROUTE_CASINO_GAME } from '../../../router/constants.js';
import { WE_SPIN_ALL_TEXT_KEYS } from '../constants/we-spin-text-keys.js';
import {
	fetchSessions,
	fetchProfile,
	fetchPrivateSessions,
	fetchParticipations,
	FETCH_SESSIONS,
	FETCH_GAME_VIEW_EVENT
} from '../ducks/we-spin-duck.js';
import {
	selectSessionsLoaded,
	selectSessionForStreamRef,
	selectIsSessionOnline,
	selectConfiguration
} from '../ducks/we-spin-selectors.js';
import {
	ROUTE_WE_SPIN_GAME_ROOM_PREVIEW,
	ROUTE_WE_SPIN_GAME_ROOM_BUY_IN,
	ROUTE_WE_SPIN_GAME_ROOM_STREAMER,
	ROUTE_WE_SPIN_GAME_ROOM_RULES,
	ROUTE_WE_SPIN_GAME_VIEW,
	ROUTE_ACCOUNT_WE_SPIN_DASHBOARD,
	ROUTE_WE_SPIN_GAME_ROOM_BUY_IN_SUCCESS,
	ROUTE_WE_SPIN_GAME_ROOM_TOC,
	ROUTE_WE_SPIN_GAME_ROOM_LIST_PLAYERS,
	ROUTE_WE_SPIN_GAME_ROOM_LIST_RAFFLE,
	ROUTE_WE_SPIN_GAME_ROOM_LIST_GAMES
} from '../constants/we-spin-route-constants.js';
import { REGISTRATION, RUNNING, SESSION_OFFLINE, SESSION_ONLINE, ENDED } from '../constants/we-spin-stream-states.js';
import StreamElement from './stream-element.js';
import WeSpinGameRoomSidebarModal from './we-spin-game-room-sidebar-modal.js';
import StartTime from '../components/start-time.js';
import useWeSpinGATracking from '../components/use-we-spin-ga-tracking.js';
import { eventTagDescriptionText } from '../constants/we-spin-event-tags.js';
import { WE_SPIN_CONTROLLER_HEAD_TITLE_DATA_AT } from '../constants/we-spin-data-at-attributes.js';
import { getWeekDayDateAndMonth } from '../utils/we-spin-time-helper.js';
import generateSrcSet from '../../../../utils/generateSrcSet.js';
import Loading from '../../../misc/loading.js';
import { fetchCurrentEvent, selectCurrentEvent, setCurrentEventRef } from '../ducks/we-spin-home-game-duck.js';

import {
	fetchFollowedStreamersList,
	setCurrentStreamerRef,
	fetchCurrentStreamerProfile,
	selectCurrentStreamerRef
} from '../ducks/we-spin-streamer-duck.js';
import styled from 'styled-components';
import { selectEventByRef, selectLiveEvent } from '../ducks/we-spin-dashboard-duck.js';

const DESKTOP_ROUTES_WITH_STREAM = [
	ROUTE_WE_SPIN_GAME_ROOM_PREVIEW,
	ROUTE_WE_SPIN_GAME_ROOM_BUY_IN,
	ROUTE_WE_SPIN_GAME_ROOM_BUY_IN_SUCCESS
];

const SIDEBAR_TITLE_SCROLL_THRESHOLD = 214;

const LiveEventBadge = styled.div`
	position: absolute;
	bottom: -12px;
	left: 50%;
	transform: translateX(-50%);
	padding: 4px 12px;
	height: 24px;
	display: flex;
	align-items: center;
	justify-content: center;
	border-radius: 3px;
	background-color: var(--search-result-item-bg-secondary);
	font-weight: 400;
	font-size: 12px;
	line-height: 16px;
	cursor: default;
`;

const LiveEventLink = styled.a`
	color: rgba(var(--primary), 1);
`;

const WeSpinGameRoomSidebarController = () => {
	const { ref: streamRef } = useParams();
	const location = useLocation();
	const navigate = useNavigate();
	const { translations, isLoadingTranslations } = useTranslations(WE_SPIN_ALL_TEXT_KEYS);
	const transformPath = useContextualPathTransformer();

	const [openModal, setOpenModal] = useState(false);
	const [isLoading, setIsLoading] = useState(false);

	const ownsThisEvent = !!useSelector(selectEventByRef(streamRef));

	const { weSpinViewListPollInterval, hasWeSpinStreamerFollowers, hasWeSpinFriends } =
		useSelector(selectConfiguration);

	const isAuthenticated = useSelector(authenticationSelector);

	const dispatch = useDispatch();
	useEffect(() => {
		setIsLoading(true);
		dispatch(fetchProfile());
		dispatch(setCurrentEventRef(streamRef));
		dispatch(fetchCurrentEvent()).then(() => {
			setIsLoading(false);
		});
	}, [dispatch, streamRef]);

	const isBigger = useSelector(isBiggerSelector);
	const streamsLoaded = useSelector(selectSessionsLoaded);
	const streamView = useSelector(selectCurrentEvent);
	const session = useSelector(selectSessionForStreamRef(streamRef));
	const isSessionOnline = useSelector(selectIsSessionOnline(streamRef)); // streamRef === sessionRef
	const currentStreamerRef = useSelector(selectCurrentStreamerRef);

	const liveEvent = useSelector(selectLiveEvent);
	const liveEventName = liveEvent
		? liveEvent?.currentGame?.gameName || liveEvent?.gameDescs?.[0]?.gameName || liveEvent?.name
		: '';

	useIntervalAction(
		FETCH_SESSIONS,
		() => {
			dispatch(fetchSessions());
			if (hasWeSpinFriends) {
				dispatch(fetchPrivateSessions());
			}
		},
		weSpinViewListPollInterval || 5000
	);
	useIntervalAction(
		FETCH_GAME_VIEW_EVENT,
		() => {
			dispatch(fetchCurrentEvent());
			dispatch(fetchParticipations({ forcedUpdate: true }));
		},
		weSpinViewListPollInterval || 5000
	);

	useWeSpinGATracking(streamRef);

	useEffect(() => {
		if (streamView?.streamerRef && streamView?.streamerRef !== currentStreamerRef) {
			dispatch(setCurrentStreamerRef(streamView?.streamerRef));
			dispatch(fetchCurrentStreamerProfile());
		}
	}, [dispatch, streamView, currentStreamerRef]);

	const streamViewRef = streamView?.streamRef;
	useEffect(() => {
		if (streamViewRef && isAuthenticated && hasWeSpinStreamerFollowers) {
			dispatch(fetchFollowedStreamersList());
		}
	}, [dispatch, streamViewRef, isAuthenticated, hasWeSpinStreamerFollowers]);

	const closeIsVisible =
		!isBigger ||
		(session && location.pathname.startsWith(ROUTE_WE_SPIN_GAME_ROOM_STREAMER)) || // Buy in but if private event only
		(location.pathname.startsWith(ROUTE_WE_SPIN_GAME_ROOM_BUY_IN) && !!streamView?.isPrivateStreamer);

	const backButtonVisible =
		location.pathname.startsWith(ROUTE_WE_SPIN_GAME_ROOM_RULES) ||
		location.pathname.startsWith(ROUTE_WE_SPIN_GAME_ROOM_TOC) ||
		location.pathname.startsWith(ROUTE_WE_SPIN_GAME_ROOM_LIST_PLAYERS) ||
		location.pathname.startsWith(ROUTE_WE_SPIN_GAME_ROOM_LIST_RAFFLE) ||
		location.pathname.startsWith(ROUTE_WE_SPIN_GAME_ROOM_LIST_GAMES) ||
		// Buy in but if not private event only
		(location.pathname.startsWith(ROUTE_WE_SPIN_GAME_ROOM_BUY_IN) && !streamView?.isPrivateStreamer) ||
		location.pathname.startsWith(ROUTE_WE_SPIN_GAME_ROOM_PREVIEW);

	const headerIcon = backButtonVisible
		? LeftSideIconType.BACK_ICON_BG
		: closeIsVisible
		? LeftSideIconType.CLOSE_ICON_BG
		: LeftSideIconType.NONE;

	const onLeftSideButtonClick = useCallback(() => {
		switch (true) {
			case location.pathname.startsWith(ROUTE_WE_SPIN_GAME_ROOM_LIST_PLAYERS):
			case location.pathname.startsWith(ROUTE_WE_SPIN_GAME_ROOM_LIST_RAFFLE):
			case location.pathname.startsWith(ROUTE_WE_SPIN_GAME_ROOM_TOC):
			case location.pathname.startsWith(ROUTE_WE_SPIN_GAME_ROOM_RULES):
			case location.pathname.startsWith(ROUTE_WE_SPIN_GAME_ROOM_LIST_GAMES):
				navigate(transformPath(`${ROUTE_WE_SPIN_GAME_ROOM_BUY_IN}/${streamRef}`));
				break;
			case location.pathname.startsWith(ROUTE_WE_SPIN_GAME_ROOM_BUY_IN):
				if (ownsThisEvent) {
					navigate(transformPath(ROUTE_ACCOUNT_WE_SPIN_DASHBOARD));
				} else {
					navigate(transformPath(ROUTE_ACCOUNT));
				}
				break;
			default:
				navigate(-1);
				break;
		}
	}, [location.pathname, navigate, ownsThisEvent, streamRef, transformPath]);

	// Take current game and filter out empty game result as {gameCode: "",gameImageUrl: "Not found",gameName: "Or enter them manually here"}
	const currentGame = streamView?.currentGame?.gameCode ? streamView?.currentGame : null;

	const gameImageUrl = currentGame?.gameImageUrl;

	const isBuyInOrPreviewView =
		location.pathname.startsWith(ROUTE_WE_SPIN_GAME_ROOM_PREVIEW) ||
		location.pathname.startsWith(ROUTE_WE_SPIN_GAME_ROOM_BUY_IN);

	const hasEventTag = streamView?.eventTags.length > 0;

	const topText = isBuyInOrPreviewView ? (
		streamView?.games?.[0]?.gameName || streamView?.name
	) : (
		<StartTime
			state={
				streamView?.state === SESSION_ONLINE
					? RUNNING
					: streamView?.state === SESSION_OFFLINE
					? REGISTRATION
					: ENDED
			}
			start={streamView?.start}
			isOnline={isSessionOnline}
		/>
	);

	const isInGameRoom = location.pathname.startsWith(ROUTE_WE_SPIN_GAME_VIEW);
	const isDesktopRouteWithStream = DESKTOP_ROUTES_WITH_STREAM.some(r => location.pathname.startsWith(r));

	const backgroundImage = streamView?.streamBgUrl || gameImageUrl || streamView?.streamerImgUrl || null;

	const backgroundImageSrcSet = backgroundImage ? generateSrcSet(backgroundImage, true) : null;
	const headerText = isBuyInOrPreviewView
		? getWeekDayDateAndMonth(streamView?.start, translations)
		: streamView?.name;

	const descriptionText = isBuyInOrPreviewView
		? hasEventTag
			? eventTagDescriptionText(streamView?.eventTags[0], translations)
			: streamView?.description
		: streamView?.description;

	const isBiggerAndDesktopInGameRoomWithStream = isBigger && isInGameRoom && isDesktopRouteWithStream;

	if (isLoadingTranslations || !streamsLoaded || !streamView) return null;

	return (
		<>
			{/* Menu modal */}
			<WeSpinGameRoomSidebarModal
				streamView={streamView}
				openModal={openModal}
				hideModalCallback={() => {
					setOpenModal(!openModal);
				}}
			/>
			<Sidebar className="wespin-sidebar">
				<SidebarScrollAware>
					{scrollYPosition => (
						<SidebarHeader
							transparent={scrollYPosition < SIDEBAR_TITLE_SCROLL_THRESHOLD}
							leftSideIcon={headerIcon}
							onLeftSideButtonClick={onLeftSideButtonClick}
							className="transparent_title"
							opaqueClassName="page-head--opac"
						>
							{!location.pathname.startsWith(ROUTE_WE_SPIN_GAME_ROOM_RULES) && (
								<H4Bold
									className="page-head__title wespin-gameroom-title"
									data-at={WE_SPIN_CONTROLLER_HEAD_TITLE_DATA_AT}
								>
									{streamView?.streamerName}
								</H4Bold>
							)}

							<div className="page-head__right-side">
								{!location.pathname.startsWith(ROUTE_WE_SPIN_GAME_ROOM_RULES) && (
									<ButtonIconBg
										onClick={() => {
											setOpenModal(!openModal);
										}}
										className="dots-container dots-container--90deg u-cursor-hand u-mr-1"
									>
										<Icon type={ICON_OPTIONS_HORIZONTAL} />
									</ButtonIconBg>
								)}
							</div>
							{!!liveEvent &&
								(streamRef !== liveEvent?.streamRef ||
									location.pathname.startsWith(ROUTE_WE_SPIN_GAME_ROOM_STREAMER)) && (
									<LiveEventBadge>
										{translations.WE_SPIN_LIVE_EVENT_LINK_TITLE}
										&nbsp;
										<LiveEventLink
											href={`${ROUTE_CASINO_GAME}/${streamView?.currentGame.gameCode}`}
											target="_blank"
											rel="noopener noreferrer"
										>
											{liveEventName}
										</LiveEventLink>
									</LiveEventBadge>
								)}
						</SidebarHeader>
					)}
				</SidebarScrollAware>
				<SidebarContent>
					{isLoading && <Loading />}
					{!isLoading && streamView?.streamRef === streamRef && (!isBigger || isDesktopRouteWithStream) && (
						<div className="top-of-page-image-wrapper u-relative">
							<StreamElement
								size="small"
								type={
									isBiggerAndDesktopInGameRoomWithStream || isBuyInOrPreviewView
										? 'IMAGE'
										: isSessionOnline
										? 'STREAM'
										: 'IMAGE'
								}
								streamLinkUrl={
									isBiggerAndDesktopInGameRoomWithStream || isBuyInOrPreviewView
										? backgroundImage
										: streamView?.streamUrl
								}
								topText={topText}
								headerText={headerText}
								streamerDescription={descriptionText}
								boost={streamView?.boost}
								showBoost={isBuyInOrPreviewView}
								srcSet={backgroundImageSrcSet}
								streamView={streamView}
								showEventTagBg={isBuyInOrPreviewView && hasEventTag}
							/>
						</div>
					)}
				</SidebarContent>
			</Sidebar>
		</>
	);
};

export default WeSpinGameRoomSidebarController;
